import { useState } from "react";
import { BackendUnavailable } from "./BackendUnavailable";

/**
 * The categories the canvas filters by. They describe the three things a
 * deployment of this application can record — a sign-in, an administrative
 * change, and a call out to a business system — and they are the filter set a
 * real endpoint would have to support.
 */
const FILTERS = ["All", "Auth", "Admin", "Integrations"] as const;
type Filter = (typeof FILTERS)[number];

interface Props {
  /** `audit.export` — the Export CSV action is its own special action. */
  canExport: boolean;
}

/**
 * Audit log.
 *
 * The canvas draws the trail as one bordered panel of rows — a monospace
 * timestamp, the category as a pill, what happened, and who did it — under a
 * row of filter pills, with Export CSV in the header.
 *
 * This application's API has no audit endpoint. The module and its actions
 * exist in the authorization manifest (`audit.read`, `audit.export`), which is
 * why this tab is gated and reachable at all, but nothing serves the entries.
 * The filters are therefore drawn and disabled rather than hidden: the shape of
 * the screen is part of the specification, and a filter that silently filtered
 * nothing would be worse than one that says it cannot run yet.
 */
export function AuditLogTab({ canExport }: Props): JSX.Element {
  const [filter, setFilter] = useState<Filter>("All");

  return (
    <section className="set-section set-section-inert" data-testid="audit-log-tab">
      <div className="set-section-head">
        <div>
          <h2>Audit log</h2>
          <p>Sign-ins, administration changes and integration calls for this deployment.</p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          disabled
          title={
            canExport
              ? "There is nothing to export until this deployment serves an audit endpoint."
              : "Exporting the audit log needs the audit.export action."
          }
          data-testid="audit-export"
        >
          Export CSV
        </button>
      </div>

      <div className="set-filter-row" role="group" aria-label="Filter the audit log">
        {FILTERS.map((option) => (
          <button
            key={option}
            type="button"
            className="set-filter"
            aria-pressed={option === filter}
            disabled
            onClick={() => setFilter(option)}
            data-testid={`audit-filter-${option.toLowerCase()}`}
          >
            {option}
          </button>
        ))}
      </div>

      <BackendUnavailable
        testId="audit-unavailable"
        what="The audit trail"
        detail={
          "The authorization manifest defines this module and its actions, which is why the tab " +
          "is here and why your role can open it — but no endpoint serves the entries, so there " +
          "is nothing to list or filter yet."
        }
        endpoints={["GET /api/v1/audit", "GET /api/v1/audit/export"]}
      />
    </section>
  );
}
